import { useState, useRef, useEffect } from "react";
import axios from "axios";
import MicButton from "@/components/accessibility/MicButton";
import ReadAloudButton from "@/components/accessibility/ReadAloudButton";
import LemonBotHelpButtons from "@/components/accessibility/LemonBotHelpButtons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LemonBot({ lessonId, suggestedPrompts }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi there! I'm LemonBot 🍋, your AI coach! I'm here to help you learn about entrepreneurship and answer any questions you have about this lesson. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasUserInteracted = useRef(false);

  const scrollToBottom = () => {
    // Only scroll after user has interacted (sent a message)
    if (hasUserInteracted.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Announce new messages to screen readers
  useEffect(() => {
    if (messages.length > 1) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === "assistant") {
        // Create a live region announcement
        const announcement = document.getElementById('lemonbot-announcement');
        if (announcement) {
          announcement.textContent = `LemonBot says: ${lastMessage.content}`;
        }
      }
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Mark that user has interacted - enable auto-scroll
    hasUserInteracted.current = true;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/lemonbot/chat`, {
        lesson_id: lessonId,
        message: userMessage
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response
      }]);
    } catch (err) {
      console.error("LemonBot error:", err);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Oops! I'm having trouble right now. Please try again in a moment! 🍋"
      }]);
    } finally {
      setLoading(false);
      // Return focus to input after response
      inputRef.current?.focus();
    }
  };

  return (
    <section 
      className="lemonbot-container rounded-2xl shadow-lg overflow-hidden" 
      data-testid="lemonbot-chat"
      aria-labelledby="lemonbot-heading"
      role="region"
    >
      {/* Screen reader announcement region */}
      <div 
        id="lemonbot-announcement" 
        className="sr-only" 
        aria-live="polite" 
        aria-atomic="true"
      ></div>

      {/* Header */}
      <header className="bg-gradient-to-r from-yellow-400 to-orange-400 px-6 py-4">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_7adefe5a-30ab-4416-ba08-2d19308841cd/artifacts/r3wtiuxz_Untitled-2_20251207_103450.png"
            alt=""
            aria-hidden="true"
            className="w-12 h-12 rounded-full"
          />
          <div>
            <h2 id="lemonbot-heading" className="text-xl font-bold text-white">LemonBot</h2>
            <p className="text-sm text-white/90">Your AI Coach</p>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div 
        className="h-96 overflow-y-auto p-6 space-y-4 bg-white" 
        data-testid="lemonbot-messages"
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
      >
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`lemonbot-message flex ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
            data-testid={`message-${idx}`}
          >
            <div
              className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                msg.role === "user"
                  ? "bg-yellow-400 text-gray-800"
                  : "bg-gray-100 text-gray-800"
              }`}
              role="article"
              aria-label={`${msg.role === "user" ? "You" : "LemonBot"} said`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              {msg.role === "assistant" && (
                <div className="mt-2 -mb-1 flex justify-end">
                  <ReadAloudButton
                    text={msg.content}
                    testId={`lemonbot-read-aloud-${idx}`}
                    inline
                    cta="Listen to feedback"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start" aria-hidden="true">
            <div className="bg-gray-100 px-4 py-3 rounded-2xl">
              <div className="flex gap-1" role="status" aria-label="LemonBot is typing">
                <span className="sr-only">LemonBot is typing...</span>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Prompts */}
      {suggestedPrompts?.length > 0 && messages.length <= 1 && (
        <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-100 flex flex-wrap gap-2" data-testid="lemonbot-prompts">
          {suggestedPrompts.map((p, i) => (
            <button
              key={i}
              onClick={() => { setInput(p); }}
              className="text-xs bg-white border border-yellow-300 text-yellow-800 px-3 py-1.5 rounded-full hover:bg-yellow-100 transition-colors"
              data-testid={`prompt-suggestion-${i}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Learning support shortcuts (accessibility) */}
      <LemonBotHelpButtons onPickPrompt={(p) => { setInput(p); inputRef.current?.focus(); }} />

      {/* Input */}
      <form 
        onSubmit={handleSend} 
        className="p-4 bg-white border-t-2 border-yellow-200"
        aria-label="Send a message to LemonBot"
      >
        <div className="flex gap-2 items-center">
          <label htmlFor="lemonbot-input" className="sr-only">
            Type your message to LemonBot
          </label>
          <input
            id="lemonbot-input"
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything about this lesson..."
            className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
            disabled={loading}
            data-testid="lemonbot-input"
            aria-describedby="lemonbot-input-hint"
            autoComplete="off"
          />
          <span id="lemonbot-input-hint" className="sr-only">
            Press Enter or click Send to submit your question. You can use the microphone button to dictate; voice is only used to turn speech into text.
          </span>
          <MicButton
            inputId="lemonbot-input"
            testId="lemonbot-mic-btn"
            onTranscript={(t) => {
              setInput((v) => (v ? v + " " : "") + t.trim());
              inputRef.current?.focus();
            }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="lemon-button px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="lemonbot-send-btn"
            aria-label={loading ? "Sending message..." : "Send message to LemonBot"}
          >
            {loading ? (
              <span aria-hidden="true">...</span>
            ) : (
              "Send"
            )}
          </button>
        </div>
      </form>
    </section>
  );
}

export default LemonBot;
